import { OptionsObject } from 'notistack';
import { MouseEventHandler } from 'react';

import { SecondaryButton } from '../buttons';
import { Flexbox } from '../Flexbox';
import { SnackbarCloseButton } from './SnackbarCloseButton';
import { SnackbarText } from './SnackbarText';

interface ActionButtonOptions {
    onClick: MouseEventHandler<HTMLButtonElement>;
    buttonText: string;
}

const defaultSnackbarOptions: Partial<OptionsObject> = {
    variant: 'info',
    preventDuplicate: true,
    anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
};

const createSnackbarOptions = (
    actionButtonOptions: ActionButtonOptions | undefined,
    snackbarOptions?: Partial<OptionsObject>,
): OptionsObject => {
    const { action: overrideAction, ...restSnackbarOptions } = snackbarOptions ?? {};

    return {
        ...defaultSnackbarOptions,
        ...restSnackbarOptions,
        action: (snackbarKey) => (
            <Flexbox
                gap={4}
                sx={{
                    alignItems: 'center',
                }}
            >
                {actionButtonOptions && (
                    <SecondaryButton
                        size="small"
                        onClick={actionButtonOptions.onClick}
                        style={{ marginLeft: '48px', marginRight: '8px' }}
                    >
                        {actionButtonOptions.buttonText}
                    </SecondaryButton>
                )}
                {typeof overrideAction === 'function' ? overrideAction(snackbarKey) : overrideAction}
                <SnackbarCloseButton snackbarKey={snackbarKey} />
            </Flexbox>
        ),
    };
};

export const createSnackbar = ({
    title,
    message,
    actionButtonOptions,
    overrides,
}: {
    title: string;
    message: string;
    actionButtonOptions?: ActionButtonOptions;
    overrides?: {
        notistackSnackbarOptions?: Partial<OptionsObject>;
    };
}): [JSX.Element, OptionsObject] => {
    const snackbarText = <SnackbarText title={title} message={message} />;
    const options = createSnackbarOptions(actionButtonOptions, overrides?.notistackSnackbarOptions);

    return [snackbarText, options];
};
